/**
 * Workflows page — list + Kanban with summary cards and search.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  alpha,
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Paper,
  LinearProgress,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Snackbar,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ViewModule as KanbanIcon,
  ViewList as ListIcon,
  DragIndicator,
  AccountTree as WorkflowIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { enhancedApi } from '../api';
import PageLayout from '../components/ui/PageLayout';
import AssignWorkflowModal from '../components/AssignWorkflowModal';

const STAGES = [
  { key: 'new', label: 'Not Started', chipColor: 'default', borderColor: 'grey.400', backendKeys: ['new'] },
  { key: 'in_progress', label: 'In Progress', chipColor: 'info', borderColor: 'info.main', backendKeys: ['gathering_info', 'in_preparation'] },
  { key: 'blocked', label: 'Blocked', chipColor: 'error', borderColor: 'error.main', backendKeys: ['in_review'] },
  { key: 'completed', label: 'Complete', chipColor: 'success', borderColor: 'success.main', backendKeys: ['completed'] },
];

const isOverdue = (dueDateStr) => {
  if (!dueDateStr) return false;
  const iso = dueDateStr.includes('T') ? dueDateStr : `${dueDateStr}T23:59:59Z`;
  return new Date(iso) < new Date();
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const initials = (name = '') =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const progressMuiColor = (pct) => {
  const p = pct ?? 0;
  if (p > 75) return 'success';
  if (p >= 50) return 'warning';
  return 'error';
};

const getStageMetaForPipeline = (pipelineStage) => STAGES.find((s) => s.backendKeys?.includes(pipelineStage)) || STAGES[0];

const WorkflowProgressBar = ({ value, sx: sxProp }) => {
  const pct = value ?? 0;
  return (
    <LinearProgress
      variant="determinate"
      value={pct}
      color={progressMuiColor(pct)}
      sx={{
        flex: 1,
        height: 6,
        borderRadius: 1,
        bgcolor: 'action.hover',
        '& .MuiLinearProgress-bar': { borderRadius: 1 },
        ...sxProp,
      }}
    />
  );
};

const WorkflowCard = ({ instance, onDragStart, onClick, borderColor }) => {
  const overdue = isOverdue(instance.due_date);
  const pct = instance.progress_percentage ?? 0;

  return (
    <Paper
      draggable
      onDragStart={(e) => onDragStart(e, instance)}
      onClick={onClick}
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: 'grab',
        borderLeft: 4,
        borderLeftColor: borderColor,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: 2 },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <DragIndicator sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>
          {instance.client_name || 'Unknown client'}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {instance.template_name || 'Workflow'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Chip
          size="small"
          label={formatDate(instance.due_date)}
          variant="outlined"
          color={overdue ? 'error' : 'default'}
          sx={{ height: 22, fontSize: 11, '& .MuiChip-label': { px: 1 } }}
        />
        {instance.assigned_to_name ? (
          <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.light' }}>{initials(instance.assigned_to_name)}</Avatar>
        ) : (
          <Box sx={{ width: 28 }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WorkflowProgressBar value={pct} sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
          {pct}%
        </Typography>
      </Box>
    </Paper>
  );
};

const SummaryCard = ({ title, value, accent, pulseError }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 140,
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      borderTop: 3,
      borderTopColor: `${accent}.main`,
      ...(pulseError
        ? {
            '@keyframes workflowOverduePulse': {
              '0%, 100%': {
                boxShadow: (theme) => `0 0 0 0 ${alpha(theme.palette.error.main, 0.35)}`,
              },
              '50%': {
                boxShadow: (theme) => `0 0 0 6px ${alpha(theme.palette.error.main, 0)}`,
              },
            },
            animation: 'workflowOverduePulse 2.4s ease-in-out infinite',
          }
        : {}),
    }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: accent === 'text.secondary' ? 'text.secondary' : `${accent}.main` }}>
      {value}
    </Typography>
  </Paper>
);

const WorkflowManagement = () => {
  const navigate = useNavigate();
  const draggedRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [searchText, setSearchText] = useState('');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [stageMap, setStageMap] = useState(Object.fromEntries(STAGES.map((s) => [s.key, []])));
  const [dragOverStage, setDragOverStage] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignUsers, setReassignUsers] = useState([]);
  const [reassignUsersLoading, setReassignUsersLoading] = useState(false);

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const res = await enhancedApi.getWorkflowPipeline();
      const rawStages = res.data?.stages ?? res.data ?? [];

      const map = Object.fromEntries(STAGES.map((s) => [s.key, []]));

      if (Array.isArray(rawStages)) {
        rawStages.forEach((stageObj) => {
          const backendKey = stageObj.id ?? stageObj.key;
          const workflows = Array.isArray(stageObj.workflows) ? stageObj.workflows : [];

          STAGES.forEach((ourStage) => {
            if (ourStage.backendKeys?.includes(backendKey)) {
              map[ourStage.key] = [...(map[ourStage.key] || []), ...workflows];
            }
          });
        });
      }

      setStageMap(map);
    } catch {
      setStageMap(Object.fromEntries(STAGES.map((s) => [s.key, []])));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const allWorkflows = Object.values(stageMap).flat();

  const filterWorkflows = (workflows) =>
    workflows.filter((w) => {
      if (!searchText) return true;
      const q = searchText.toLowerCase();
      return (
        (w.client_name || '').toLowerCase().includes(q) ||
        (w.template_name || '').toLowerCase().includes(q) ||
        (w.organizer_name || '').toLowerCase().includes(q)
      );
    });

  const handleDragStart = (e, instance) => {
    draggedRef.current = instance;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(instance.id));
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  };

  const handleDragLeave = () => setDragOverStage(null);

  const getBackendKeyForStage = (stageKey) => {
    const stage = STAGES.find((s) => s.key === stageKey);
    return stage?.backendKeys?.[0] ?? stageKey;
  };

  const handleDrop = async (e, targetStageKey) => {
    e.preventDefault();
    setDragOverStage(null);

    const instance = draggedRef.current;
    draggedRef.current = null;
    if (!instance) return;

    const targetBackendKey = getBackendKeyForStage(targetStageKey);
    const sourceStage = STAGES.find((s) => s.backendKeys?.includes(instance.pipeline_stage));
    const sourceKey = sourceStage?.key ?? 'new';
    if (sourceKey === targetStageKey && instance.pipeline_stage === targetBackendKey) return;

    setStageMap((prev) => {
      const next = { ...prev };
      STAGES.forEach((s) => {
        next[s.key] = (prev[s.key] || []).filter((w) => w.id !== instance.id);
      });
      next[targetStageKey] = [...(next[targetStageKey] || []), { ...instance, pipeline_stage: targetBackendKey }];
      return next;
    });

    setMovingId(instance.id);
    try {
      await enhancedApi.moveWorkflowStage(instance.id, targetBackendKey);
      setSnackbar({ open: true, message: 'Workflow updated' });
    } catch {
      fetchPipeline();
    } finally {
      setMovingId(null);
    }
  };

  const handleCardClick = (instance) => {
    if (instance?.client_id) {
      navigate(`/client/${instance.client_id}`);
    }
  };

  const handleCompleteWorkflow = async (row) => {
    const backendKey = getBackendKeyForStage('completed');
    setMovingId(row.id);
    try {
      await enhancedApi.moveWorkflowStage(row.id, backendKey);
      setSnackbar({ open: true, message: 'Workflow completed' });
      await fetchPipeline();
    } catch {
      setSnackbar({ open: true, message: 'Could not complete workflow' });
      fetchPipeline();
    } finally {
      setMovingId(null);
    }
  };

  const openReassign = async (row) => {
    setReassignTarget(row);
    if (reassignUsers.length === 0) {
      setReassignUsersLoading(true);
      try {
        const res = await enhancedApi.getUsers();
        const userList = Array.isArray(res.data) ? res.data : res.data?.users || res.data?.data || [];
        setReassignUsers(
          userList.map((u) => ({
            id: u.id,
            label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`,
          }))
        );
      } catch {
        setReassignUsers([]);
      } finally {
        setReassignUsersLoading(false);
      }
    }
  };

  const applyReassign = async (userId) => {
    if (!reassignTarget) return;
    setMovingId(reassignTarget.id);
    try {
      await enhancedApi.updateWorkflow(reassignTarget.id, { assigned_to: userId });
      setSnackbar({ open: true, message: 'Assignee updated' });
      setReassignTarget(null);
      await fetchPipeline();
    } catch {
      setSnackbar({ open: true, message: 'Could not reassign workflow' });
      fetchPipeline();
    } finally {
      setMovingId(null);
    }
  };

  const filteredByStage = Object.fromEntries(STAGES.map((s) => [s.key, filterWorkflows(stageMap[s.key] || [])]));

  const flattenedFiltered = Object.values(filteredByStage).flat();

  const isEmpty = allWorkflows.length === 0;
  const hasSearchNoResults = !!searchText && flattenedFiltered.length === 0 && !isEmpty;

  const totalCount = allWorkflows.length;
  const activeCount = allWorkflows.filter((w) => (w.progress_percentage ?? 0) < 100).length;
  const overdueCount = allWorkflows.filter((w) => isOverdue(w.due_date) && (w.progress_percentage ?? 0) < 100).length;
  const completedCount = allWorkflows.filter((w) => (w.progress_percentage ?? 0) >= 100).length;

  return (
    <PageLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '60vh' }}>
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ flex: '1 1 auto', minWidth: 200 }}>
            <Typography variant="h5" fontWeight={700} component="h1">
              Workflows
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {activeCount} active workflows
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <TextField
              size="small"
              placeholder="Search…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" startIcon={<WorkflowIcon />} onClick={() => navigate('/workflows/templates')}>
              Templates
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setAssignModalOpen(true)}>
              Assign Workflow
            </Button>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
              <ToggleButton value="list" aria-label="List view">
                <ListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="kanban" aria-label="Kanban view">
                <KanbanIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Summary cards */}
        {!loading && !isEmpty && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <SummaryCard title="Total" value={totalCount} variant="primary" />
            <SummaryCard title="Active" value={activeCount} variant="success" />
            <SummaryCard title="Overdue" value={overdueCount} variant="error" pulseError={overdueCount > 0} />
            <SummaryCard title="Completed" value={completedCount} variant="neutral" />
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isEmpty ? (
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.default',
            }}
          >
            <WorkflowIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              No workflows yet
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setAssignModalOpen(true)} sx={{ mt: 2 }}>
              Assign your first workflow
            </Button>
          </Paper>
        ) : viewMode === 'kanban' ? (
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, alignItems: 'flex-start', minHeight: 400 }}>
            {STAGES.map((stage) => {
              const workflows = filteredByStage[stage.key] || [];
              const isDragTarget = dragOverStage === stage.key;

              return (
                <Box
                  key={stage.key}
                  sx={{
                    flex: '0 0 260px',
                    minWidth: 260,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onDragOver={(e) => handleDragOver(e, stage.key)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage.key)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      borderBottom: 2,
                      borderBottomColor: stage.borderColor,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {stage.label}
                    </Typography>
                    <Chip label={workflows.length} size="small" sx={{ height: 20, fontSize: 11 }} />
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 120,
                      p: 0.5,
                      borderRadius: 1.5,
                      border: isDragTarget ? 2 : 0,
                      borderStyle: 'dashed',
                      borderColor: isDragTarget ? stage.borderColor : 'transparent',
                      bgcolor: isDragTarget ? 'action.selected' : 'transparent',
                      transition: 'border 0.15s, background-color 0.15s',
                    }}
                  >
                    {workflows.length === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 100,
                          borderRadius: 1.5,
                          border: '1px dashed',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          No workflows
                        </Typography>
                      </Box>
                    ) : (
                      workflows.map((instance) => (
                        <Box key={instance.id} sx={{ position: 'relative' }}>
                          {movingId === instance.id && (
                            <CircularProgress size={14} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }} />
                          )}
                          <WorkflowCard
                            instance={instance}
                            onDragStart={handleDragStart}
                            borderColor={stage.borderColor}
                            onClick={() => handleCardClick(instance)}
                          />
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Paper sx={{ overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <DataGrid
              rows={flattenedFiltered}
              getRowId={(r) => r.id}
              columns={[
                {
                  field: 'client_name',
                  headerName: 'Client',
                  flex: 1,
                  minWidth: 200,
                  sortable: false,
                  renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.light' }}>
                        {initials(params.row.client_name)}
                      </Avatar>
                      {params.row.client_id ? (
                        <Link
                          component={RouterLink}
                          to={`/client/${params.row.client_id}`}
                          underline="hover"
                          color="primary"
                          variant="body2"
                          fontWeight={600}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {params.row.client_name || '—'}
                        </Link>
                      ) : (
                        <Typography variant="body2">{params.row.client_name || '—'}</Typography>
                      )}
                    </Box>
                  ),
                },
                {
                  field: 'template_name',
                  headerName: 'Workflow Name',
                  flex: 1,
                  minWidth: 160,
                  // v7+: (value, row, column, apiRef). Older builds passed GridValueGetterParams as first arg only.
                  valueGetter: (valueOrParams, row) => {
                    if (valueOrParams != null && typeof valueOrParams === 'object' && 'row' in valueOrParams) {
                      return valueOrParams.row?.template_name || '—';
                    }
                    return row?.template_name ?? (typeof valueOrParams === 'string' ? valueOrParams : null) ?? '—';
                  },
                },
                {
                  field: 'stage',
                  headerName: 'Stage',
                  width: 140,
                  sortable: false,
                  renderCell: (params) => {
                    const meta = getStageMetaForPipeline(params.row.pipeline_stage);
                    return (
                      <Chip
                        label={meta.label}
                        size="small"
                        color={meta.chipColor}
                        variant={meta.chipColor === 'default' ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 600, fontSize: 12 }}
                      />
                    );
                  },
                },
                {
                  field: 'progress_percentage',
                  headerName: 'Progress',
                  width: 180,
                  renderCell: (params) => {
                    const pct = params.value ?? 0;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                        <WorkflowProgressBar value={pct} />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36 }}>
                          {pct}%
                        </Typography>
                      </Box>
                    );
                  },
                },
                {
                  field: 'due_date',
                  headerName: 'Due Date',
                  width: 130,
                  renderCell: (params) => (
                    <Typography
                      variant="body2"
                      fontWeight={isOverdue(params.row.due_date) ? 600 : 400}
                      sx={{ color: isOverdue(params.row.due_date) ? 'error.main' : 'text.primary' }}
                    >
                      {formatDate(params.row.due_date)}
                    </Typography>
                  ),
                },
                {
                  field: 'assigned_to_name',
                  headerName: 'Assigned',
                  width: 150,
                  renderCell: (params) =>
                    params.value ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 11 }}>{initials(params.value)}</Avatar>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                          {params.value}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ),
                },
                {
                  field: 'actions',
                  headerName: 'Actions',
                  width: 220,
                  sortable: false,
                  disableColumnMenu: true,
                  renderCell: (params) => {
                    const done = (params.row.progress_percentage ?? 0) >= 100;
                    return (
                      <Box
                        className="workflow-row-actions"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          opacity: 0,
                          transition: 'opacity 0.15s',
                        }}
                      >
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (params.row.client_id) navigate(`/client/${params.row.client_id}`);
                          }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="success"
                          disabled={done || movingId === params.row.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompleteWorkflow(params.row);
                          }}
                        >
                          Complete
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          disabled={movingId === params.row.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            openReassign(params.row);
                          }}
                        >
                          Reassign
                        </Button>
                      </Box>
                    );
                  },
                },
              ]}
              autoHeight
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': { fontSize: '0.875rem', alignItems: 'center', display: 'flex' },
                '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' },
                '& .MuiDataGrid-row:hover .workflow-row-actions': { opacity: 1 },
              }}
            />
          </Paper>
        )}

        {hasSearchNoResults && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            No workflows match your search
          </Typography>
        )}
      </Box>

      <AssignWorkflowModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onSuccess={() => {
          fetchPipeline();
          setAssignModalOpen(false);
        }}
      />

      <Dialog open={!!reassignTarget} onClose={() => setReassignTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reassign workflow</DialogTitle>
        <DialogContent dividers>
          {reassignUsersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense disablePadding>
              {reassignUsers.map((u) => (
                <ListItemButton key={u.id} onClick={() => applyReassign(u.id)}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 12 }}>{initials(u.label)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={u.label} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageLayout>
  );
};

export default WorkflowManagement;
